import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatPrice } from "../lib/api";
import { toast } from "sonner";
import { Copy, Upload, CheckCircle2, Lock, FileText, Building2, Hash } from "lucide-react";
import { PayPalCheckout } from "./PayPalCheckout";

const CopyRow = ({ label, value }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiato`);
    } catch { toast.error("Copia non riuscita"); }
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-white/10 last:border-0">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</p>
        <p className="text-sm text-white font-medium break-all">{value || "—"}</p>
      </div>
      {value && (
        <button
          type="button"
          onClick={copy}
          className="shrink-0 p-2 text-white/50 hover:text-gold transition-colors"
          data-testid={`copy-${label.toLowerCase()}`}
          aria-label={`Copia ${label}`}
        >
          <Copy size={14} />
        </button>
      )}
    </div>
  );
};

export const ReservationDialog = ({ open, onOpenChange, car }) => {
  const [settings, setSettings] = useState(null);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [step, setStep] = useState("info"); // info -> form -> paypal | receipt -> done
  const [method, setMethod] = useState("bank_transfer"); // bank_transfer | paypal
  const [reservationId, setReservationId] = useState(null);
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", notes: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("info");
    setMethod("bank_transfer");
    setReservationId(null);
    setForm({ name: "", surname: "", email: "", phone: "", notes: "" });
    setFile(null);
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/paypal/config")
      .then((r) => setPaypalEnabled(Boolean(r.data?.enabled && r.data?.client_id)))
      .catch(() => setPaypalEnabled(false));
  }, [open]);

  if (!car) return null;
  const causal = car.ref_code
    ? `Caparra ${car.ref_code} - Cognome cliente`
    : `Caparra ${car.brand} ${car.model} ${car.year} - Cognome cliente`;

  const submitReservation = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surname || !form.email || !form.phone) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (method === "paypal") {
      setStep("paypal");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/reservations", { ...form, car_id: car.id });
      setReservationId(r.data.id);
      setStep("receipt");
      toast.success("Prenotazione registrata. Ora puoi caricare la ricevuta (opzionale).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore prenotazione");
    } finally { setLoading(false); }
  };

  const uploadReceipt = async () => {
    if (!file) { setStep("done"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/reservations/${reservationId}/receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Ricevuta caricata");
      setStep("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload ricevuta");
    } finally { setLoading(false); }
  };

  const deposit = Math.round((car.price || 0) * 0.10);
  const depositPct = "10%";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-midnight text-white border-white/10 rounded-sm p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="reservation-dialog"
      >
        <div className="p-6 md:p-8">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gold/15 flex items-center justify-center">
                <Lock size={16} className="text-gold" />
              </div>
              <div>
                <DialogTitle className="font-display text-2xl text-white leading-tight">
                  Blocca questa auto
                </DialogTitle>
                <DialogDescription className="text-white/60 text-xs mt-1">
                  {car.brand} {car.model} · {car.year}
                  {car.ref_code && (
                    <span className="ml-2 font-mono text-gold tracking-wider">· {car.ref_code}</span>
                  )}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {step === "info" && (
            <div className="mt-6 space-y-5" data-testid="reservation-step-info">
              {paypalEnabled && (
                <div className="grid grid-cols-2 gap-2" data-testid="payment-method-tabs">
                  <button
                    type="button"
                    onClick={() => setMethod("bank_transfer")}
                    className={`py-3 px-4 text-[11px] uppercase tracking-widest font-semibold rounded-sm border transition-colors ${
                      method === "bank_transfer"
                        ? "bg-gold text-midnight border-gold"
                        : "border-white/15 text-white/60 hover:border-gold/50"
                    }`}
                    data-testid="method-bank-tab"
                  >
                    Bonifico
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("paypal")}
                    className={`py-3 px-4 text-[11px] uppercase tracking-widest font-semibold rounded-sm border transition-colors ${
                      method === "paypal"
                        ? "bg-gold text-midnight border-gold"
                        : "border-white/15 text-white/60 hover:border-gold/50"
                    }`}
                    data-testid="method-paypal-tab"
                  >
                    PayPal · Carta
                  </button>
                </div>
              )}

              <div className="bg-gold/10 border border-gold/30 p-4">
                <p className="text-sm text-white/90 leading-relaxed">
                  Per bloccare questa auto è richiesta una <span className="text-gold font-semibold">caparra pari al {depositPct} del valore dell'auto</span> — nel tuo caso <span className="text-gold font-semibold">{formatPrice(deposit)}</span>{" "}
                  {method === "paypal" ? "tramite PayPal o carta di credito" : "tramite bonifico bancario"}.
                </p>
              </div>

              {method === "bank_transfer" ? (
                <>
                  {!settings || !settings.iban ? (
                    <div className="border border-white/10 p-4 text-sm text-white/70">
                      Le coordinate bancarie non sono ancora state pubblicate. Contatta Rocco per procedere con la prenotazione.
                    </div>
                  ) : (
                    <div className="border border-white/10 divide-white/10">
                      <div className="p-4">
                        <CopyRow label="Beneficiario" value={settings.account_holder} />
                        <CopyRow label="Banca" value={settings.bank_name} />
                        <CopyRow label="IBAN" value={settings.iban} />
                        {settings.bic_swift && <CopyRow label="BIC/SWIFT" value={settings.bic_swift} />}
                        <CopyRow label="Importo caparra" value={formatPrice(deposit)} />
                        <CopyRow label="Causale" value={causal} />
                      </div>
                    </div>
                  )}
                  {settings?.payment_instructions && (
                    <div className="text-xs text-white/60 leading-relaxed whitespace-pre-line">
                      {settings.payment_instructions}
                    </div>
                  )}
                  <button
                    onClick={() => setStep("form")}
                    className="btn-gold w-full py-3 text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2"
                    data-testid="proceed-to-form-btn"
                  >
                    Ho effettuato il bonifico — Conferma i miei dati
                  </button>
                  <p className="text-[11px] text-white/40 text-center leading-relaxed">
                    La caparra è vincolante. Una volta ricevuta, Rocco ti contatterà per completare l'acquisto.
                  </p>
                </>
              ) : (
                <>
                  <div className="border border-white/10 p-4 text-xs text-white/70 leading-relaxed">
                    Paga la caparra online con PayPal, carta di credito o debito. Al ricevimento del pagamento, Rocco ti contatterà per organizzare il ritiro.
                  </div>
                  <button
                    onClick={() => setStep("form")}
                    className="btn-gold w-full py-3 text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2"
                    data-testid="proceed-to-paypal-form-btn"
                  >
                    Continua con PayPal
                  </button>
                </>
              )}
            </div>
          )}

          {step === "form" && (
            <form onSubmit={submitReservation} className="mt-6 space-y-4" data-testid="reservation-step-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-white/60">Nome *</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/15 rounded-sm text-white"
                    data-testid="res-name-input"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-white/60">Cognome *</Label>
                  <Input
                    required
                    value={form.surname}
                    onChange={(e) => setForm({ ...form, surname: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/15 rounded-sm text-white"
                    data-testid="res-surname-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/60">Email *</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/15 rounded-sm text-white"
                  data-testid="res-email-input"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/60">Telefono *</Label>
                <Input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/15 rounded-sm text-white"
                  data-testid="res-phone-input"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/60">Note</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Data del bonifico, banca di provenienza, richieste particolari..."
                  className="mt-1.5 bg-white/5 border-white/15 rounded-sm text-white"
                  data-testid="res-notes-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep("info")}
                  className="flex-1 py-3 text-xs uppercase tracking-widest border border-white/20 hover:border-white/40"
                >
                  Indietro
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold flex-1 py-3 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                  data-testid="submit-reservation-btn"
                >
                  {loading ? "Invio..." : "Conferma Prenotazione"}
                </button>
              </div>
            </form>
          )}

          {step === "paypal" && (
            <div className="mt-6 space-y-4" data-testid="reservation-step-paypal">
              <div className="bg-emerald-500/5 border border-emerald-500/20 p-4">
                <p className="text-xs text-white/70 leading-relaxed">
                  Stai per pagare la caparra di <span className="text-gold font-semibold">{formatPrice(deposit)}</span> per{" "}
                  <span className="text-white">{car.brand} {car.model} ({car.year})</span>.
                </p>
              </div>
              <PayPalCheckout
                carId={car.id}
                customer={form}
                onSuccess={(rid) => { setReservationId(rid); setStep("done"); }}
              />
              <button
                type="button"
                onClick={() => setStep("form")}
                className="w-full py-2 text-[10px] uppercase tracking-widest text-white/50 hover:text-white/80"
                data-testid="paypal-back-btn"
              >
                ← Indietro
              </button>
            </div>
          )}

          {step === "receipt" && (
            <div className="mt-6 space-y-5" data-testid="reservation-step-receipt">
              <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-white font-semibold">Prenotazione registrata</p>
                  <p className="text-xs text-white/70 mt-1">
                    Puoi allegare la ricevuta del bonifico ora oppure inviarla via email in un secondo momento.
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Ricevuta bonifico (opzionale)
                </Label>
                <label className="border-2 border-dashed border-white/15 hover:border-gold hover:bg-white/5 transition-colors p-6 flex flex-col items-center justify-center cursor-pointer">
                  <Upload size={22} className="text-gold mb-2" />
                  <p className="text-sm text-white/80">
                    {file ? file.name : "Clicca per caricare"}
                  </p>
                  <p className="text-[10px] text-white/40 mt-1">JPG, PNG, PDF · Max 8MB</p>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*,application/pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="receipt-file-input"
                  />
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep("done")}
                  className="flex-1 py-3 text-xs uppercase tracking-widest border border-white/20 hover:border-white/40"
                  data-testid="skip-receipt-btn"
                >
                  Salta per ora
                </button>
                <button
                  type="button"
                  onClick={uploadReceipt}
                  disabled={loading || !file}
                  className="btn-gold flex-1 py-3 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                  data-testid="upload-receipt-btn"
                >
                  {loading ? "Caricamento..." : "Carica Ricevuta"}
                </button>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="mt-6 text-center py-6" data-testid="reservation-step-done">
              <CheckCircle2 size={54} className="text-gold mx-auto mb-4" />
              <h3 className="font-display text-2xl mb-3">Prenotazione completata</h3>
              <p className="text-sm text-white/70 leading-relaxed max-w-sm mx-auto mb-6">
                Rocco riceverà la tua richiesta e verificherà il bonifico. Ti contatterà appena possibile ai recapiti forniti.
              </p>
              <button
                onClick={() => onOpenChange(false)}
                className="btn-gold px-8 py-3 text-xs uppercase tracking-widest font-semibold"
                data-testid="close-reservation-btn"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationDialog;
